import { useState, useEffect, useRef } from 'react';
import { IoNotificationsOutline, IoNotifications, IoClose, IoCheckmark } from 'react-icons/io5';
import { useNotifications } from '../contexts/NotificationContext';
import './NotificationBell.css';

const NotificationBell = () => {
  const { 
    notifications, 
    unreadCount, 
    loading,
    error,
    markAsRead, 
    markAllAsRead,
    refreshNotifications 
  } = useNotifications();
  
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      refreshNotifications();
    }
  };

  const handleMarkAsRead = async (notificationId, event) => {
    event.stopPropagation();
    if (notifications.find(notification => notification.id === notificationId)?.isRead) return;
    await markAsRead(notificationId, 'clicked');
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const getNotificationIcon = (type) => {
    const icons = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      error: '❌',
      announcement: '📢',
      reminder: '⏰',
      achievement: '🏆'
    };
    return icons[type] || '📢';
  };

  const getNotificationColor = (type) => {
    const colors = {
      info: '#3B82F6',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      announcement: '#8B5CF6',
      reminder: '#06B6D4',
      achievement: '#F97316'
    };
    return colors[type] || '#6B7280';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const displayedNotifications = showAll ? notifications : notifications.slice(0, 5);
  const hasMoreNotifications = notifications.length > 5;

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button 
        className="notification-bell-button"
        onClick={toggleDropdown}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        {unreadCount > 0 ? (
          <IoNotifications className="notification-icon" />
        ) : (
          <IoNotificationsOutline className="notification-icon" />
        )}
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-button"
                onClick={handleMarkAllAsRead}
              >
                Mark all read
              </button>
            )}
            <button 
              className="close-button"
              aria-label="Close notifications"
              onClick={() => setIsOpen(false)}
            >
              <IoClose size={16} />
            </button>
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">Loading notifications...</div>
            ) : error ? (
              <div className="notification-empty" role="alert">
                <p>{error}</p>
                <button onClick={refreshNotifications}>Try again</button>
              </div>
            ) : displayedNotifications.length === 0 ? (
              <div className="notification-empty">
                <IoNotificationsOutline size={32} />
                <p>No notifications</p>
              </div>
            ) : (
              displayedNotifications.map((notification) => (
                <div 
                  key={notification.id} 
                  className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                  onClick={(event) => handleMarkAsRead(notification.id, event)}
                >
                  <div className="notification-icon-container">
                    <span 
                      className="notification-type-icon"
                      style={{ color: getNotificationColor(notification.type) }}
                    >
                      {getNotificationIcon(notification.type)}
                    </span>
                  </div>
                  
                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.title}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-meta">
                      <span className="notification-time">
                        {formatTime(notification.createdAt)}
                      </span>
                      {notification.category && (
                        <span className="notification-category">
                          {notification.category}
                        </span>
                      )}
                    </div>
                  </div>

                  {!notification.isRead && (
                    <button 
                      className="mark-read-button"
                      onClick={(e) => handleMarkAsRead(notification.id, e)}
                      title="Mark as read"
                    >
                      <IoCheckmark size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {hasMoreNotifications && (
            <div className="notification-footer">
              <button 
                className="show-more-button"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? 'Show less' : `Show ${notifications.length - 5} more`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
